import { ChoiceIdV1, ChoiceId, CharacterId, AnimationKey, CharSlotId, StepId, TimelineId } from '../../types';
import { IBubble, ICamera, ICharSlot, ICharSlots, IGesture, ITimelines, IStep, ISteps, IGestures } from '../../validation/schema/v2/story';
import { DialogLike } from '../types';
import { DialogStep, DialogStepGesture, DialogCharacter,  DialogStepCamera, DialogStepBubble } from '../../validation/schema/v1/story';
import { INITIAL_STEP, INITIAL_TIMELINE } from '../../StoryTeller';
/**
 * !!! This is a MAJOR migration !!!
 * This migration got introduced because we need a better way to handle timeline data and navigation
 * so we can introduce minted choices
 */

const timelineIdMap: Record<ChoiceIdV1, TimelineId> = {
  a: 't2',
  b: 't3',
  c: 't4',
}

const newChoiceIdMap: Record<ChoiceIdV1, ChoiceId> = {
  a: 'c1',
  b: 'c2',
  c: 'c3',
}

const charSlotIdMap: Record<CharacterId, CharSlotId> = {
  a: 'cs1',
  b: 'cs2',
  c: 'cs3',
}

const getNewGestures = (gestures: DialogStep['gestures']): IGestures => {
  const newGestures = gestures.reduce((res: IGestures, cur: DialogStepGesture) => {
    const charSlotId = charSlotIdMap[cur.charID as CharacterId];
    return {
      ...res,
      [charSlotId]: {
        charSlotId,
        dirX: cur.dirX,
        animation: cur.gesture as AnimationKey,
        loop: cur.loop,
        charPos: cur.charPos,
        charMotion: cur.charMotion,
      }
    }
  }, {} as IGestures);
  return newGestures;
}

const getNewCamera = (camera?: DialogStepCamera): ICamera | undefined => {
  if (!camera) {
    return undefined;
  }
  const newCamera = {
    ...camera,
    charSlotId: camera.charID ? charSlotIdMap[camera.charID as CharacterId] : undefined,
  }
  delete newCamera.charID;
  return newCamera;
}

const getNewBubble = (bubble?: DialogStepBubble): IBubble | undefined => {
  if (!bubble) {
    return undefined;
  }
  const newBubble = {
    ...bubble,
    charSlotId: bubble.charID ? charSlotIdMap[bubble.charID as CharacterId] : undefined,
  }
  delete newBubble.charID;
  return newBubble;
}

const getStepProps = ({gestures, parallax, camera, bubble, choices}: DialogStep) => ({
  gestures: getNewGestures(gestures),
  parallax,
  camera: getNewCamera(camera),
  bubble: getNewBubble(bubble),
  choices,
});

const getNewSteps = (firstStep: DialogStep, steps: DialogStep[]) => {

  const stepsObj: ISteps = {};

  let step: DialogStep | undefined = firstStep;
  let count = 1;

  // if we only have the first step
  if (steps.length === 1 && firstStep === steps[0]) {
    stepsObj[`s${count}`] = {
      ...getStepProps(step),
      nextStep: undefined,
      prevStep: undefined,
    } as IStep;
    return stepsObj;
  }


  while(step?.navigation.next) {
    const key: StepId = `s${count}`;
    const nextStep: StepId = `s${count+1}`;
    const prevStep = count === 1 ? undefined : `s${count-1}`;

    stepsObj[key] = {
      ...getStepProps(step),
      nextStep,
      prevStep,
    } as IStep;

    count ++;
    step = steps.find(s => s.navigation.id === step?.navigation.next);

    // is the last step in this timeline
    if (step && !step?.navigation.next) {
      const choices = step.choices ? (Object.keys(step.choices) as ChoiceIdV1[]).reduce((res, cur) => {
        const { caption } = (step!.choices as any)[cur];
        return {
          ...res,
          [newChoiceIdMap[cur]]: {
            toTimeline: timelineIdMap[cur],
            caption,
          }
        }
      }, {}) as any : undefined;

      stepsObj[nextStep] = {
        ...getStepProps(step),
        choices,
        prevStep: `s${count-1}`,
        nextStep: undefined,
      }
    }
  }

  return stepsObj;
} 

const getOrderedStepKeys = (steps: any) => {
  return (Object.keys(steps) as StepId[]).sort((a, b) => {
    return Number(a.replace('s', '')) - Number(b.replace('s', ''));
  }) 
}

export const migrateToTimelines = async (dialog: DialogLike): Promise<DialogLike> => {

  const steps = dialog.steps as DialogStep[];
  
  const rootTimelineId = INITIAL_TIMELINE;

  const timelines: ITimelines = {};

  const rootSteps: DialogStep[] = [];

  const timelineSteps: Record<ChoiceIdV1 | string, DialogStep[]> = {};

  const choices = steps.find(s => s.choices)?.choices as any;

  for (const step of steps) {
    const parent = step.navigation.parent;
    if (!parent) {
      rootSteps.push(step);
      continue;
    }

    if (choices) {
      if (choices.a.navigation.parent === parent) {
        if (!timelineSteps.a) {
          timelineSteps.a = [];
        }
        timelineSteps.a.push(step);
      } else if (choices.b?.navigation.parent === parent) {
        if (!timelineSteps.b) {
          timelineSteps.b = [];
        }
        timelineSteps.b.push(step);
      } else if (choices.c?.navigation.parent === parent) {
        if (!timelineSteps.c) {
          timelineSteps.c = [];
        }
        timelineSteps.c.push(step);
      }
    }
  }

  const initialStep: string = dialog.initialStep;

  let step = rootSteps.find(s => s.navigation.id === initialStep);

  if (!step) {
    throw new Error(`Something really wrong happened. Cannot find step with 'initialStep' in 'rootSteps'`);
  }

  const newSteps = getNewSteps(step, rootSteps) as any;

  const stepIds = getOrderedStepKeys(newSteps);
  
  timelines[rootTimelineId] = {
    root: rootTimelineId,
    lastStep: stepIds[stepIds.length-1],
    firstStep: INITIAL_STEP,
    steps: newSteps,
    toTimeline: undefined, // @TODO: compute this properly
    mergeTimeline: undefined,
  }

  if (choices) {
    for (const choiceId of Object.keys(timelineSteps)) {

      const timelineId = timelineIdMap[choiceId as ChoiceIdV1];

      const steps = timelineSteps[choiceId];

      const initialStep = steps.find(s => s.navigation.parent?.includes(s.navigation.id));

      if (!initialStep) {
        throw new Error(`Error: Could not find 'initialStep' for timeline '${timelineId}'`);
      }

      const newSteps = getNewSteps(initialStep, steps) as any;
  
      const stepIds = getOrderedStepKeys(newSteps);
  
      timelines[timelineId] = {
        root: rootTimelineId,
        lastStep: stepIds[stepIds.length-1],
        firstStep: INITIAL_STEP,
        steps: newSteps,
        toTimeline: undefined, // @TODO: This has to be properly computed
        mergeTimeline: undefined,
      }  
    }
  }

  const charSlots: ICharSlot = (Object.keys(dialog.characters) as CharacterId[]).reduce((res, cur) => {
    const id = charSlotIdMap[cur];

    const char = dialog.characters[cur] as DialogCharacter;

    const charMetadata = dialog.charactersMetadata[cur];

    const charSlot: ICharSlot = {
      ...char,
      id,
      charMetadata,
      overrideable: undefined,
    }

    return {
      ...res,
      [id]: charSlot
    }
  }, {} as ICharSlot);

  const data = {
    ...dialog,
    rootTimelineId,
    timelines,
    charSlots,
    choicesMade: {},
    // Clear deprecated props
    characters: undefined,
    charactersMetadata: undefined,
    parallax: undefined,
    initialStep: undefined,
    steps: undefined,
    timestamp: undefined,
    lastVersion: undefined,
    version: undefined,
    saveCount: undefined,
    editedBy: undefined,
  };

  // Remove undefined props
  return JSON.parse(JSON.stringify(data));
};
